// Returns Management Types

// ============================================================================
// Status & Enum Types
// ============================================================================

export type ReturnRequestStatus =
  | "pending"
  | "receiving"
  | "inspecting"
  | "routing"
  | "completed"
  | "cancelled";

export const RETURN_REQUEST_STATUS_LABELS: Record<ReturnRequestStatus, string> =
  {
    pending: "Pendiente",
    receiving: "Recibiendo",
    inspecting: "Inspeccionando",
    routing: "Ubicando",
    completed: "Completado",
    cancelled: "Cancelado",
  };

export type ReturnItemStatus =
  | "pending"
  | "received"
  | "inspected"
  | "routed"
  | "cancelled";

export const RETURN_ITEM_STATUS_LABELS: Record<ReturnItemStatus, string> = {
  pending: "Pendiente",
  received: "Recibido",
  inspected: "Inspeccionado",
  routed: "Ubicado",
  cancelled: "Cancelado",
};

export type DispositionType =
  | "SELLABLE"
  | "REPACKAGE"
  | "DISCARD"
  | "SUPPLIER_RETURN";

export const DISPOSITION_LABELS: Record<DispositionType, string> = {
  SELLABLE: "Vendible (A)",
  REPACKAGE: "Re-empacar (B)",
  DISCARD: "Descartar (C)",
  SUPPLIER_RETURN: "Devolver a Proveedor (D)",
};

export const DISPOSITION_COLORS: Record<DispositionType, string> = {
  SELLABLE: "bg-green-100 text-green-800 border-green-200",
  REPACKAGE: "bg-yellow-100 text-yellow-800 border-yellow-200",
  DISCARD: "bg-red-100 text-red-800 border-red-200",
  SUPPLIER_RETURN: "bg-purple-100 text-purple-800 border-purple-200",
};

export type ReturnType = "full" | "partial" | "exchange";

export const RETURN_TYPE_LABELS: Record<ReturnType, string> = {
  full: "Devolución Total",
  partial: "Devolución Parcial",
  exchange: "Cambio",
};

// ============================================================================
// Return Reason Codes
// ============================================================================

export type ReturnReasonCategory =
  | "customer"
  | "quality"
  | "operational"
  | "supplier";

export type ReturnReasonCode =
  // Customer-initiated
  | "WRONG_ORDER"
  | "DUPLICATE_ORDER"
  | "REJECTED_DELIVERY"
  | "OVER_ORDER"
  | "BUSINESS_CLOSURE"
  | "CHANGE_OF_MIND"
  // Quality
  | "DAMAGED_TRANSIT"
  | "DAMAGED_WAREHOUSE"
  | "QUALITY_COMPLAINT"
  | "NEAR_EXPIRATION"
  | "EXPIRED"
  | "CONTAMINATION"
  // Operational
  | "WRONG_PRODUCT"
  | "WRONG_QUANTITY"
  | "WRONG_ADDRESS"
  | "LATE_DELIVERY"
  | "PRICING_ERROR"
  // Supplier
  | "DEFECTIVE_PRODUCT"
  | "RECALL";

export const RETURN_REASON_LABELS: Record<ReturnReasonCode, string> = {
  // Customer-initiated
  WRONG_ORDER: "Pedido Incorrecto",
  DUPLICATE_ORDER: "Pedido Duplicado",
  REJECTED_DELIVERY: "Entrega Rechazada",
  OVER_ORDER: "Exceso de Pedido",
  BUSINESS_CLOSURE: "Cierre de Negocio",
  CHANGE_OF_MIND: "Cambio de Opinión",
  // Quality
  DAMAGED_TRANSIT: "Daño en Tránsito",
  DAMAGED_WAREHOUSE: "Daño en Bodega",
  QUALITY_COMPLAINT: "Reclamo de Calidad",
  NEAR_EXPIRATION: "Próximo a Vencer",
  EXPIRED: "Vencido",
  CONTAMINATION: "Contaminación",
  // Operational
  WRONG_PRODUCT: "Producto Incorrecto",
  WRONG_QUANTITY: "Cantidad Incorrecta",
  WRONG_ADDRESS: "Dirección Incorrecta",
  LATE_DELIVERY: "Entrega Tardía",
  PRICING_ERROR: "Error de Precio",
  // Supplier
  DEFECTIVE_PRODUCT: "Producto Defectuoso",
  RECALL: "Retiro del Mercado",
};

export const RETURN_REASONS_BY_CATEGORY: Record<
  ReturnReasonCategory,
  ReturnReasonCode[]
> = {
  customer: [
    "WRONG_ORDER",
    "DUPLICATE_ORDER",
    "REJECTED_DELIVERY",
    "OVER_ORDER",
    "BUSINESS_CLOSURE",
    "CHANGE_OF_MIND",
  ],
  quality: [
    "DAMAGED_TRANSIT",
    "DAMAGED_WAREHOUSE",
    "QUALITY_COMPLAINT",
    "NEAR_EXPIRATION",
    "EXPIRED",
    "CONTAMINATION",
  ],
  operational: [
    "WRONG_PRODUCT",
    "WRONG_QUANTITY",
    "WRONG_ADDRESS",
    "LATE_DELIVERY",
    "PRICING_ERROR",
  ],
  supplier: ["DEFECTIVE_PRODUCT", "RECALL"],
};

export const RETURN_REASON_CATEGORY_LABELS: Record<
  ReturnReasonCategory,
  string
> = {
  customer: "Cliente",
  quality: "Calidad",
  operational: "Operacional",
  supplier: "Proveedor",
};

// ============================================================================
// Discard Reason Codes
// ============================================================================

export type DiscardReasonCategory =
  | "physical"
  | "expiration"
  | "contamination"
  | "other";

export type DiscardReasonCode =
  // Physical damage
  | "CRACKED_BOTTLES"
  | "LEAKING"
  | "CRUSHED_PACKAGING"
  | "PUNCTURED"
  // Expiration
  | "EXPIRED_DISCARD"
  | "NEAR_EXPIRY_DISCARD"
  | "SHELF_LIFE_TOO_SHORT"
  // Contamination
  | "CONTAMINATED"
  | "PEST_DAMAGE"
  | "WATER_DAMAGE"
  // Other
  | "LABEL_DAMAGE"
  | "QUALITY_FAIL"
  | "OTHER";

export const DISCARD_REASON_LABELS: Record<DiscardReasonCode, string> = {
  // Physical damage
  CRACKED_BOTTLES: "Botellas Rajadas",
  LEAKING: "Fugas",
  CRUSHED_PACKAGING: "Empaque Aplastado",
  PUNCTURED: "Perforado",
  // Expiration
  EXPIRED_DISCARD: "Vencido",
  NEAR_EXPIRY_DISCARD: "Próximo a Vencer",
  SHELF_LIFE_TOO_SHORT: "Vida Útil Corta",
  // Contamination
  CONTAMINATED: "Contaminado",
  PEST_DAMAGE: "Daño por Plagas",
  WATER_DAMAGE: "Daño por Agua",
  // Other
  LABEL_DAMAGE: "Etiqueta Dañada",
  QUALITY_FAIL: "Falla de Calidad",
  OTHER: "Otro",
};

export const DISCARD_REASONS_BY_CATEGORY: Record<
  DiscardReasonCategory,
  DiscardReasonCode[]
> = {
  physical: ["CRACKED_BOTTLES", "LEAKING", "CRUSHED_PACKAGING", "PUNCTURED"],
  expiration: [
    "EXPIRED_DISCARD",
    "NEAR_EXPIRY_DISCARD",
    "SHELF_LIFE_TOO_SHORT",
  ],
  contamination: ["CONTAMINATED", "PEST_DAMAGE", "WATER_DAMAGE"],
  other: ["LABEL_DAMAGE", "QUALITY_FAIL", "OTHER"],
};

export const DISCARD_REASON_CATEGORY_LABELS: Record<
  DiscardReasonCategory,
  string
> = {
  physical: "Daño Físico",
  expiration: "Vencimiento",
  contamination: "Contaminación",
  other: "Otro",
};

// ============================================================================
// Main Entity Types
// ============================================================================

export interface ReturnRequest {
  id: string;
  return_number: string;
  credit_note_id?: string;
  credit_note_number?: string;
  original_sales_request_id?: string;
  client_id?: string;
  return_type: ReturnType;
  status: ReturnRequestStatus;
  priority: number;
  assigned_to?: string;
  assigned_at?: string;
  started_at?: string;
  completed_at?: string;
  notes?: string;
  internal_notes?: string;
  created_at: string;
  updated_at: string;
  // Joined data
  client?: {
    id: string;
    name: string;
    tax_id?: string;
  };
  original_sales_request?: {
    id: string;
    created_at: string;
  };
  assigned_profile?: {
    name: string;
    lastname: string;
  };
  items?: ReturnRequestItem[];
}

export interface ReturnRequestItem {
  id: string;
  return_request_id: string;
  product_id: string;
  expected_quantity: number;
  received_quantity: number;
  return_reason: ReturnReasonCode;
  return_reason_notes?: string;
  original_batch_id?: string;
  original_lot_number?: string;
  status: ReturnItemStatus;
  created_at: string;
  updated_at: string;
  // Joined data
  product?: {
    id: string;
    short_name: string;
    long_name?: string;
  };
  original_batch?: {
    id: string;
    batch_number: string;
    expiry_date?: string;
  };
  inspections?: ReturnInspection[];
}

export interface ReturnInspection {
  id: string;
  return_request_item_id: string;
  disposition: DispositionType;
  quantity: number;
  discard_reason?: DiscardReasonCode;
  destination_warehouse_id?: string;
  destination_position_id?: string;
  photo_url?: string;
  notes?: string;
  inspected_by?: string;
  inspected_at: string;
  routed_at?: string;
  created_at: string;
  // Joined data
  destination_warehouse?: {
    id: string;
    code: string;
    name: string;
  };
  destination_position?: {
    id: string;
    full_code: string;
    barcode?: string;
  };
  inspector?: {
    name: string;
    lastname: string;
  };
}

// ============================================================================
// Form Data Types
// ============================================================================

export interface ReturnRequestFormData {
  credit_note_id?: string;
  credit_note_number?: string;
  original_sales_request_id?: string;
  client_id?: string;
  return_type: ReturnType;
  priority?: number;
  notes?: string;
  items: ReturnRequestItemFormData[];
}

export interface ReturnRequestItemFormData {
  product_id: string;
  expected_quantity: number;
  return_reason: ReturnReasonCode;
  return_reason_notes?: string;
  original_batch_id?: string;
  original_lot_number?: string;
}

export interface ReturnInspectionFormData {
  return_request_item_id: string;
  disposition: DispositionType;
  quantity: number;
  discard_reason?: DiscardReasonCode;
  destination_warehouse_id?: string;
  notes?: string;
  photo_url?: string;
}

export interface ReturnReceiveFormData {
  return_request_item_id: string;
  received_quantity: number;
  notes?: string;
}

export interface ReturnPutawayFormData {
  inspection_id: string;
  destination_position_id: string;
}

// ============================================================================
// Filter Types
// ============================================================================

export interface ReturnRequestFilters {
  status?: ReturnRequestStatus | ReturnRequestStatus[];
  client_id?: string;
  assigned_to?: string;
  return_type?: ReturnType;
  priority?: number;
  date_from?: string;
  date_to?: string;
}

// ============================================================================
// Statistics Types
// ============================================================================

export interface ReturnStatistics {
  pending_returns: number;
  receiving_returns: number;
  inspecting_returns: number;
  routing_returns: number;
  completed_today: number;
  total_items_pending: number;
  disposition_breakdown: {
    sellable: number;
    repackage: number;
    discard: number;
    supplier_return: number;
  };
}

export interface ReturnDispositionSummary {
  disposition: DispositionType;
  quantity: number;
  percentage: number;
}

// ============================================================================
// Warehouse Type Extensions
// ============================================================================

export type WarehouseType =
  | "active"
  | "returns_staging"
  | "discard"
  | "repackaging"
  | "supplier_returns";

export const WAREHOUSE_TYPE_LABELS: Record<WarehouseType, string> = {
  active: "Activo",
  returns_staging: "Staging Devoluciones",
  discard: "Descarte",
  repackaging: "Re-empaque",
  supplier_returns: "Devoluciones Proveedor",
};

export const DISPOSITION_TO_WAREHOUSE_TYPE: Record<
  DispositionType,
  WarehouseType
> = {
  SELLABLE: "active",
  REPACKAGE: "repackaging",
  DISCARD: "discard",
  SUPPLIER_RETURN: "supplier_returns",
};
